import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Swal from "sweetalert2";
import { customerservice } from "../../../../config";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../../data/DynamicServiceNew/jsondynamicservice";
import {
  isValidKenyanMobile,
  isValidIdNumber,
  sanitizeIdInput,
  sanitizeMobileInput,
} from "../customerValidation";

const CustomerMobileMigration = () => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userRoleGroup = parseInt(decodedToken.RoleGroupId);
  const userId = parseInt(decodedToken.Id);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCustomer, setFilteredCustomer] = useState(null);
  const [newValue, setNewValue] = useState(""); // Renamed from fieldOfficerSelected for clarity
  const [isIdTransfer, setIsIdTransfer] = useState(false); // Checkbox state for ID/Phone transfer
const [runTour, setRunTour] = useState(false);

const steps = [
  {
    target: ".joyride-search-input",
    content: "Start by searching for a customer using phone number or ID.",
  },
  {
    target: ".joyride-customer-details",
    content: "Here you can see the customer details.",
  },
  {
    target: ".joyride-toggle-transfer",
    content: "Toggle this to choose between updating ID or phone number.",
  },
  {
    target: ".joyride-new-value",
    content: "Enter the new ID number or phone number here.",
  },
  {
    target: ".joyride-submit-button",
    content: "Click here to submit the changes.",
  },
];

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm) {
        try {
          const isPhoneNumber = searchTerm.length >= 10;
          const variables = isPhoneNumber
            ? { mobileNumber: searchTerm }
            : { idNoOrPassport: searchTerm };

          const response = await fetchJsonDynamic(
            "customersByColumnValuesLatest",
            "customer",
            variables,
            customerservice.uri
          );

          const customerData = JSON.parse(response.results);

          if (customerData && customerData.length > 0) {
            setFilteredCustomer(customerData[0]);
          } else {
            setFilteredCustomer(null);
            Swal.fire(
              "Customer Not Found",
              "Customer does not exist",
              "warning"
            );
          }
        } catch (error) {
          console.error("Error fetching customer:", error);
          setFilteredCustomer(null);
          Swal.fire("Error", "Failed to search customer", "error");
        }
      } else {
        setFilteredCustomer(null);
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCheckboxChange = (e) => {
    setIsIdTransfer(e.target.checked);
    setNewValue(""); // Clear input when switching between ID and phone
  };

  // Passports can be alphanumeric; infer from the customer's current ID value
  const isPassportCustomer = /\D/.test(filteredCustomer?.IdNoOrPassport || "");
  const isNewValueValid = isIdTransfer
    ? isValidIdNumber(newValue, isPassportCustomer)
    : isValidKenyanMobile(newValue);
  const validationMessage = isIdTransfer
    ? isPassportCustomer
      ? "Passport number must be at least 7 characters with no spaces"
      : "ID number must be at least 7 digits (numbers only)"
    : "Must start with 01, 07 or 254 (digits only, 10 or 12 digits)";

  const handleCustomerMigrationSubmit = async () => {
    if (!isNewValueValid) {
      Swal.fire({
        icon: "warning",
        title: isIdTransfer ? "Invalid ID Number" : "Invalid Mobile Number",
        text: validationMessage,
      });
      return;
    }
    try {
      const mutationData = isIdTransfer
        ? {
            p_CustomerId: filteredCustomer.Id,
            p_Idnumber: newValue, // For ID transfer
          }
        : {
            p_CustomerId: filteredCustomer.Id,
            p_PhoneNumber: newValue, // For phone transfer
          };

      const mutationName = isIdTransfer
        ? "customerNationalIdTransfer"
        : "customerPhonenumberTransfer";

      const inputType = isIdTransfer
        ? "id_Number_Transfer"
        : "phone_Number_Transfer";

      const response = await executeAnyMutation(
        mutationName,
        mutationData,
        inputType,
        false,
        customerservice.uri
      );

      if (response.results === "200") {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: isIdTransfer
            ? "Customer ID updated successfully"
            : "Customer phone number updated successfully",
        }).then(() => {
          setSearchTerm("");
          setFilteredCustomer(null);
          setNewValue("");
          setIsIdTransfer(false);
        });
      } else if (response.results === "-10") {
        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: isIdTransfer
            ? "Customer ID already changed awaiting approval"
            : "Customer phone already changed awaiting approval",
        }).then(() => {
          setSearchTerm("");
          setFilteredCustomer(null);
          setNewValue("");
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: response.error || "An unexpected error occurred.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Joyride
  steps={steps}
  run={runTour}
  continuous
  showSkipButton
  showProgress
  styles={{ options: { primaryColor: "#1976d2", zIndex: 9999 } }}
  callback={(data) => {
    if (["finished", "skipped"].includes(data.status)) {
      setRunTour(false);
    }
  }}
/>
<Button variant="outlined" onClick={() => setRunTour(true)} sx={{ mb: 2 }}>
  Start Tour
</Button>

      <Typography variant="h5">Search Customer:</Typography>
      <TextField
        className="joyride-search-input"
        label="Search Customer by phone number | ID No. | Passport"
        placeholder="Enter search criteria"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={handleSearchChange}
      />

      {filteredCustomer && (
        <>
          <Card variant="outlined" sx={{ mb: 2 }} className="joyride-customer-details">
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  backgroundColor: "#003049",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: 1,
                }}
              >
                Customer Details
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Customer Name:</strong> {filteredCustomer.FirstName}{" "}
                    {filteredCustomer.OtherNames}
                  </Typography>
                  <Typography>
                    <strong>Email:</strong> {filteredCustomer.EmailAddress}
                  </Typography>
                  <Typography>
                    <strong>ID Number:</strong>{" "}
                    {filteredCustomer.IdNoOrPassport}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Phone Number:</strong>{" "}
                    {filteredCustomer.MobileNumber}
                  </Typography>
                  <Typography>
                    <strong>Branch:</strong> {filteredCustomer.BranchName || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>Date Created:</strong>{" "}
                    {filteredCustomer.DateCreated}
                  </Typography>
                  <Typography>
                    <strong>Current Loan Limit:</strong>{" "}
                    {filteredCustomer.LoanLimit}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  backgroundColor: "#003049",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: 1,
                }}
              >
                {isIdTransfer ? "Change ID Number" : "Change Phone Number"}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControlLabel
                    className="joyride-toggle-transfer"
                    control={
                      <Checkbox
                        checked={isIdTransfer}
                        onChange={handleCheckboxChange}
                        name="isIdTransfer"
                      />
                    }
                    label="Change ID Number (instead of Phone Number)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      className="joyride-new-value"
                      fullWidth
                      label={isIdTransfer ? "New ID Number" : "New Phone Number"}
                      value={newValue}
                      onChange={(e) =>
                        setNewValue(
                          isIdTransfer
                            ? sanitizeIdInput(e.target.value, isPassportCustomer)
                            : sanitizeMobileInput(e.target.value)
                        )
                      }
                      inputProps={isIdTransfer ? undefined : { inputMode: "numeric", maxLength: 12 }}
                      error={Boolean(newValue) && !isNewValueValid}
                      helperText={newValue && !isNewValueValid ? validationMessage : ""}
                      placeholder={
                        isIdTransfer ? "Enter ID number" : "e.g. 0712345678 or 254712345678"
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Button
            className="joyride-submit-button"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleCustomerMigrationSubmit}
            disabled={!newValue || !isNewValueValid}
          >
            {isIdTransfer ? "Change ID Number" : "Change Phone Number"}
          </Button>
        </>
      )}
    </Box>
  );
};

export default CustomerMobileMigration;